// วงจรชีวิตของหนึ่งกรอบยิงตรง — รวมไว้ที่เดียวให้ VideoCell เหลือแค่วาด UI · กรอบ webview อยู่ที่ WebviewCell

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CELL, FIRST_FRAME_TIMEOUT_MS } from './constants'
import { sendEvent } from './livestreamApi'
import {
  PLAYER,
  canRefreshFor,
  deriveCellState,
  toCellError,
} from './cellState'
import useDirectSession from './useDirectSession'
import useFirstFrameTimeout from './useFirstFrameTimeout'
import usePageVisible from './usePageVisible'
import usePlayerStatus from './usePlayerStatus'

const NO_SOURCE = { protocol: null, url: null }

/** discovery / camera / onRediscover — ดู useDirectSession · onRediscover = ปุ่มลองใหม่ถามกล้องใหม่ทั้งกรอบ */
export default function useCellSession({
  vehicle,
  discovery,
  camera,
  onRediscover,
}) {
  // ห้ามผูก dependency กับ object `vehicle` — ตารางรถรีเฟรชทุก 60 วิเป็น object ใหม่
  // effect จะมองว่าเปลี่ยนรถแล้วรีเซ็ตกรอบที่เล่นอยู่ทุกนาที ใช้ค่า primitive เท่านั้น
  const vehicleId = vehicle ? vehicle.id : null
  const hasVehicle = vehicleId !== null && vehicleId !== undefined

  const direct = useDirectSession({
    enabled: hasVehicle,
    vehicleId,
    discovery,
    camera,
  })
  const { retry: retryDirect, refreshStream, release, changeCamera } = direct

  const streamUrl = direct.stream ? direct.stream.url : null
  const streamProtocol = direct.stream ? direct.stream.protocol : null

  const source = useMemo(
    () =>
      hasVehicle && streamUrl
        ? { protocol: streamProtocol, url: streamUrl }
        : NO_SOURCE,
    [hasVehicle, streamUrl, streamProtocol],
  )

  // key ของ PlayerHost — retry แล้วได้ URL เดิม (join stream เดิม) ก็ต้อง
  // สร้าง player ใหม่ ไม่งั้นตัวที่ค้าง error อยู่จะไม่โหลดซ้ำ
  const { attempt } = direct
  const playerKey = `${source.url || ''}#${attempt}`

  const { status, writerFor, isCurrent } = usePlayerStatus(playerKey)
  const write = useMemo(() => writerFor(playerKey), [writerFor, playerKey])
  const statusRef = useRef(status)
  statusRef.current = status

  const sessionId = direct.session ? direct.session.id : null
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  const track = useCallback((event, detail) => {
    const id = sessionIdRef.current
    if (id) sendEvent(id, event, detail)
  }, [])

  // first_frame ครั้งเดียวต่อ session — URL หมุน (token ใหม่) แล้ว player สร้างใหม่ ไม่ใช่ภาพแรก
  const firstFrameOf = useRef(null)

  // แท็บถูกซ่อน เบราว์เซอร์ไม่โหลดภาพ → หยุดนับ กลับมาแสดงแล้วเริ่มนับใหม่ครบเวลา
  const visible = usePageVisible()
  const waitingFirstFrame =
    visible &&
    !!source.url &&
    (status.player === PLAYER.IDLE || status.player === PLAYER.CONNECTING)
  const { protocol } = source

  const onFirstFrameTimeout = useCallback(() => {
    write({ timedOut: true })
    track('no_first_frame', { protocol, waited_ms: FIRST_FRAME_TIMEOUT_MS })
  }, [write, track, protocol])

  useFirstFrameTimeout(waitingFirstFrame, playerKey, onFirstFrameTimeout)

  // ขอ URL ใหม่ได้ 1 ครั้งต่อ session จนกว่าจะเล่นได้ — กัน backend ที่หมุน token ทุก GET วนไม่จบ
  // ผูกกับ session ไม่ใช่ attempt เพราะเปิดใหม่แล้ว session ใหม่ต้องได้สิทธิ์ใหม่
  const refreshGate = useRef({ session: null, used: false })

  /** player ที่พังถาวรแล้ว — รายงานที่ตามมาของตัวนั้น (ค้าง / เล่นต่อ) ไม่นับ */
  const failedKey = useRef(null)

  const resumeRef = useRef(null)

  const handlers = useMemo(() => {
    const key = playerKey
    const alive = () => isCurrent(key)
    const usable = () => alive() && failedKey.current !== key

    return {
      onConnecting: () =>
        write(s =>
          s.player === PLAYER.IDLE ? { player: PLAYER.CONNECTING } : null,
        ),
      onPlaying: sample => {
        if (!usable()) return
        refreshGate.current.used = false
        write({ player: PLAYER.PLAYING, error: null })
        const id = sessionIdRef.current
        if (id && firstFrameOf.current !== id) {
          firstFrameOf.current = id
          track('first_frame', sample)
        }
      },
      onStall: sample => {
        if (!usable()) return
        write({ player: PLAYER.STALLED })
        track('stalled', sample)
      },
      onResume: sample => {
        if (!usable()) return
        write({ player: PLAYER.PLAYING })
        track('recovered', sample)
      },
      onSpeed: kbps => write({ speed: kbps }),
      onBlocked: resume => {
        if (!usable()) return
        resumeRef.current = resume
        write({ player: PLAYER.BLOCKED })
      },
      onError: (code, detail, sample) => {
        if (!usable()) return
        const error = toCellError({ code, detail, retryable: true })
        const qos = { ...sample, player_error: `${code}: ${detail}` }
        track('player_error', qos)

        const fail = () => {
          if (!alive()) return // กรอบไปรอบใหม่แล้ว — error นี้ไม่เกี่ยว
          failedKey.current = key
          write({ player: PLAYER.ERROR, error })
          // ไม่มีใครดูได้แล้ว — ปล่อย session ทันที อย่าให้กล้องส่งภาพค้างให้จอที่ขึ้น error
          release()
        }

        const gate = refreshGate.current
        const gateUsed = gate.used && gate.session === sessionIdRef.current
        if (!canRefreshFor(code) || gateUsed) {
          fail()
          return
        }
        refreshGate.current = { session: sessionIdRef.current, used: true }

        refreshStream().then(renewed => {
          // ได้ URL ใหม่ → playerKey เปลี่ยน → VideoCell สร้าง player ตัวใหม่ให้เอง
          if (renewed) track('url_expired', qos)
          else fail()
        })
      },
    }
  }, [playerKey, isCurrent, write, track, refreshStream, release])

  const state = deriveCellState({
    hasVehicle,
    timedOut: status.timedOut,
    deviceOnline: direct.deviceOnline,
    phase: direct.phase,
    directError: direct.error,
    player: status.player,
  })

  const error = useMemo(() => {
    if (state !== CELL.ERROR && state !== CELL.OFFLINE) return null
    if (direct.error) return direct.error
    if (status.player === PLAYER.BLOCKED) return toCellError('autoplay_blocked')
    if (status.error) return status.error
    if (status.timedOut) return toCellError('no_first_frame')
    return null
  }, [state, direct.error, status.player, status.error, status.timedOut])

  // นับตั้งแต่กรอบเริ่มรอ ไม่ใช่ตั้งแต่สร้าง session — URL หมุน / retry / เปลี่ยนกล้อง ก็นับใหม่
  const waiting = state === CELL.STARTING || state === CELL.CONNECTING
  const [waitingSince, setWaitingSince] = useState(null)
  useEffect(() => {
    setWaitingSince(waiting ? Date.now() : null)
  }, [waiting, attempt])

  const retry = useCallback(() => {
    // เล่นอัตโนมัติถูกบล็อก: ต้องสั่ง play() ใน click handler นี้ตรง ๆ (ห้าม await ก่อน)
    // ไม่ต้องเปิด session ใหม่ — สตรีมยังดีอยู่ แค่เบราว์เซอร์รอให้ผู้ใช้กด
    const resume = resumeRef.current
    if (statusRef.current.player === PLAYER.BLOCKED && resume) {
      resumeRef.current = null
      write({ player: PLAYER.CONNECTING })
      resume()
      return
    }
    if (onRediscover) onRediscover()
    else retryDirect()
  }, [onRediscover, retryDirect, write])

  return {
    state,
    source,
    playerKey,
    error,
    endReason: direct.endReason,
    speed: status.speed,
    cameras: direct.cameras,
    camera: direct.camera,
    waitingSince,
    sessionId,
    handlers,
    retry,
    changeCamera,
  }
}
